export interface RetirementGoalOptions {
  targetAsset: number;
  currentAsset: number;
  currentAge: number;
  retirementAge: number;
  expectedReturnRate: number;
}

export interface RetirementGoalResult {
  requiredMonthlySaving: number;
  futureValueOfCurrentAsset: number;
  additionalNeeded: number;
  totalContributions: number;
  totalInterest: number;
  alreadyAchieved: boolean;
  years: number;
}

/**
 * PMT 공식:
 * r = expectedReturnRate / 100 / 12
 * n = (retirementAge - currentAge) * 12
 * FV_current = currentAsset * (1+r)^n
 * remaining = max(0, targetAsset - FV_current)
 * PMT = remaining * r / ((1+r)^n - 1)   [r > 0]
 * PMT = remaining / n                    [r = 0]
 */
export function calculateRetirementGoal(options: RetirementGoalOptions): RetirementGoalResult {
  const { targetAsset, currentAsset, currentAge, retirementAge, expectedReturnRate } = options;
  const years = retirementAge - currentAge;
  const r = expectedReturnRate / 100 / 12;
  const n = years * 12;
  const growth = Math.pow(1 + r, n);

  const futureValueOfCurrentAsset = Math.round(currentAsset * growth);
  const additionalNeeded = Math.max(0, targetAsset - futureValueOfCurrentAsset);
  const alreadyAchieved = additionalNeeded === 0;

  let pmt = 0;
  if (!alreadyAchieved && n > 0) {
    pmt = r === 0 ? additionalNeeded / n : (additionalNeeded * r) / (growth - 1);
  }
  const requiredMonthlySaving = Math.round(pmt);
  const totalContributions = requiredMonthlySaving * n;
  const totalInterest = Math.max(0, Math.round(targetAsset - currentAsset - totalContributions));

  return {
    requiredMonthlySaving,
    futureValueOfCurrentAsset,
    additionalNeeded,
    totalContributions,
    totalInterest,
    alreadyAchieved,
    years,
  };
}
